<script>
import MultiselectSearch from "@/components/multiselect-search.vue";
import urls from "@/constants/urls";
import { notificationsMixin } from "@/mixins";
import axios from "axios";
import { defineComponent } from "vue";

export default defineComponent({
  components: {
    MultiselectSearch,
  },

  mixins: [notificationsMixin],

  props: {
    campaignId: { type: [Number, String], required: true },
    orgId: { type: [Number, String], required: true },
    team: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      selectedTeams: [],
      localValue: null,
    };
  },

  computed: {
    valueModel: {
      get() {
        return this.localValue;
      },
      set(val) {
        this.localValue = val;
      },
    },
    skipTeamIDList() {
      const skipList = this.selectedTeams.map((t) => t.team_id);
      if (this.team.id) {
        skipList.push(this.team.id);
      }
      if (this.team.parent_team_id) {
        skipList.push(this.team.parent_team_id);
      }
      return skipList;
    },
    getSearchUrl() {
      return urls.apiTeams.replace(":orgId", this.orgId).replace(":campaignId", this.campaignId);
    },
    searchOptions() {
      return {
        placeholder: this.$t("dashboard.select_team", "Select teams"),
        changeParams: ({ query, page, per_page: pp }) => {
          return {
            q: query,
            page: page,
            limit: pp,
            skip_team_id: this.skipTeamIDList,
          };
        },
        customLabel: (e) => `[${e.team_id}] ${e.name}`,
        processResults: (data) =>
          data.map((el) => ({
            team_id: Number(el.id),
            ...el.attributes,
          })),
      };
    },
  },

  watch: {
    selectedTeams: {
      handler(newVal) {
        this.$emit("hasChildren", newVal.length > 0);
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.getChildrenList();
  },

  methods: {
    getChildrenList() {
      this.loading = true;
      return axios
        .get(urls.teamRelations(this.orgId, this.campaignId, this.team.id))
        .then((res) => {
          const list = res.data.data;
          this.selectedTeams = list;
        })
        .finally(() => (this.loading = false));
    },
    async $save() {
      if (this.loading || this.disabled) return;
      this.loading = true;
      const payload = {
        children_team_id_list: this.selectedTeams.map((t) => t.team_id),
      };
      return axios
        .post(urls.teamRelations(this.orgId, this.campaignId, this.team.id), payload)
        .catch((err) => {
          if (err.response?.data?.errors?.length === 0) {
            this.$_notificationsMixin_makeToast("Error!", err.response.data.message, "danger");
          }
          err.response.data.errors.forEach((e) => {
            let tk = `edit_campaign.${e.code}`;
            if (e.code) {
              tk = `${tk}_${e.field}`;
            }
            this.$_notificationsMixin_makeToast("Error!", this.$t(tk, e.message), "danger");
          });
          return Promise.reject(err);
        })
        .finally(() => (this.loading = false));
    },

    addTeamToSelected() {
      if (!this.localValue) return;
      if (this.selectedTeams.find((t) => t.team_id === this.localValue.team_id)) {
        this.$_notificationsMixin_makeToast(
          "Error!",
          this.$t("dashboard.team_search_already_added", "Team already added"),
          "danger"
        );
        return;
      }
      this.selectedTeams.push(this.localValue);
      this.localValue = null;
    },
    removeTeamToSelected(i) {
      this.selectedTeams.splice(i, 1);
    },
  },
});
</script>

<template>
  <div class="d-flex flex-column gap-3" :class="{ 'opacity-50': disabled }">
    <b-list-group>
      <b-list-group-item v-for="(t, i) in selectedTeams" :key="t.team_id">
        <div class="tw-flex tw-justify-between tw-items-center">
          <span>[{{ t.team_id }}] {{ t.name }}</span>
          <b-icon-x-circle-fill
            v-if="!disabled"
            variant="danger"
            class="tw-cursor-pointer"
            @click="removeTeamToSelected(i)"
          ></b-icon-x-circle-fill>
        </div>
      </b-list-group-item>
    </b-list-group>

    <div v-if="!disabled" class="tw-flex tw-gap-4">
      <multiselect-search
        v-model="valueModel"
        :open-prefetch="true"
        :allow-empty="true"
        :disabled="loading"
        :search-url="getSearchUrl"
        :search-options="searchOptions"
        :close-on-select="true"
        class="w-100"
      >
        <template #no-options>
          {{ $t("dashboard.team_search_no_options", "Please enter 1 or more characters") }}
        </template>
        <template #no-more-infinite>
          {{ $t("dashboard.team_search_no_more", "No more teams") }}
        </template>
      </multiselect-search>

      <button :disabled="!localValue" class="bttn bttn--lg bttn--orange" @click="addTeamToSelected">
        {{ $t("dashboard.team_search_add", "Add") }}
      </button>
    </div>
    <div v-else>
      <b-spinner v-if="loading" class="tw-mt-3" />
      <span v-else class="text-muted">
        {{
          $t("dashboard.team_search_disabled", "Team selection is disabled as parent team selected")
        }}
      </span>
    </div>
  </div>
</template>
